// Widget Test Page - Embed and test widget configuration

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { widgetService } from '../services/widget';
import type { WidgetEmbedCode } from '../types/api';
import styles from './WidgetTestPage.module.css';

export function WidgetTestPage() {
  const { widgetId } = useParams<{ widgetId: string }>();
  const [embedInfo, setEmbedInfo] = useState<WidgetEmbedCode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadWidget() {
      if (!widgetId) {
        setError('No widget ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const code = await widgetService.getEmbedCode(widgetId, 'bottom-right');
        setEmbedInfo(code);

        // Inject the widget script
        const script = document.createElement('script');
        script.src = `${code.api_base_url}/widget/chat.js`;
        script.setAttribute('data-widget-id', widgetId);
        script.setAttribute('data-position', 'bottom-right');
        document.body.appendChild(script);

        // Cleanup on unmount
        return () => {
          // Remove the script
          script.remove();
          // Remove the widget elements
          const widgetContainer = document.getElementById('cm-chat-widget-container');
          if (widgetContainer) {
            widgetContainer.remove();
          }
        };
      } catch (err) {
        console.error('Failed to load widget:', err);
        setError('Failed to load widget. Make sure the widget ID is valid and the widget is active.');
      } finally {
        setIsLoading(false);
      }
    }

    loadWidget();
  }, [widgetId]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1>Widget Test Page</h1>

        {isLoading && (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <span>Loading widget...</span>
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {embedInfo && !error && (
          <div className={styles.info}>
            <h2>Widget: {embedInfo.widget_name}</h2>
            <p>
              The chat widget should appear in the bottom-right corner of this page.
              Click the chat button to test the widget functionality.
            </p>

            <div className={styles.details}>
              <h3>Widget Configuration</h3>
              <ul>
                <li>
                  <strong>Widget ID:</strong>{' '}
                  <code>{embedInfo.widget_id}</code>
                </li>
                <li>
                  <strong>API Base URL:</strong>{' '}
                  <code>{embedInfo.api_base_url}</code>
                </li>
                <li>
                  <strong>Allowed Domains:</strong>{' '}
                  {embedInfo.allowed_domains.length > 0
                    ? embedInfo.allowed_domains.join(', ')
                    : 'None configured'}
                </li>
              </ul>
            </div>

            <div className={styles.testArea}>
              <h3>Test Scenarios</h3>
              <ul>
                <li>Try opening the chat and sending a message</li>
                <li>Test minimizing and maximizing the widget</li>
                <li>Check that the greeting message appears correctly</li>
                <li>Test the response time and quality</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
